import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { finalize, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { FeatureFlags, FeatureKey, OnboardingProfile } from '../models/onboarding.model';

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly http = inject(HttpClient);
  private readonly base = '/api/v1/onboarding';

  readonly profile = signal<OnboardingProfile | null>(null);
  readonly loading = signal(false);

  readonly requiresOnboarding = computed(() => {
    const p = this.profile();
    return !!p && !p.onboardingComplete;
  });

  load(): Observable<OnboardingProfile> {
    this.loading.set(true);
    return this.http.get<OnboardingProfile>(this.base).pipe(
      tap(profile => this.profile.set(profile)),
      finalize(() => this.loading.set(false)),
    );
  }

  save(features: FeatureFlags): Observable<OnboardingProfile> {
    this.loading.set(true);
    return this.http.put<OnboardingProfile>(this.base, { features }).pipe(
      tap(profile => this.profile.set(profile)),
      finalize(() => this.loading.set(false)),
    );
  }

  isEnabled(feature: FeatureKey): boolean {
    const p = this.profile();
    if (!p) return true;
    return !!p.features?.[feature];
  }

  reset(): void {
    this.profile.set(null);
    this.loading.set(false);
  }
}
